import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { openCalendly } from "@/lib/calendly";
import PackageExamplesModal from "@/components/PackageExamplesModal";

const packages = [
    {
        id: "monthly",
        tag: "Signature",
        name: "Event/Offer Monthly Strategy",
        price: "$400",
        cadence: "/ month",
        desc: "A monthly revenue-driving event or promotional offer designed to bring in guests, increase sales, and build repeat business.",
        goal: "Irresistible offers and events that pack the house, increase check averages, and create community buzz.",
        heroImage: "https://customer-assets.emergentagent.com/job_drink-food-expert/artifacts/tf59cvcb_priscilla-du-preez-W3SEyZODn8U-unsplash%20%281%29.jpg",
        heroAlt: "A long communal patio table packed with guests eating shared plates and clinking glasses of beer and cocktails under string lights — exactly the lively, sold-out crowd a monthly event concept brings in",
        includes: [
            "Monthly event or offer concept development",
            "Execution strategy & sales positioning",
            "Financial goal setting and reporting",
            "2 emails, 4 social posts, 1 SMS per month",
            "Eventbrite & reservation platform setup",
            "Upsell, add-on & retail strategy",
            "Lead capture & loyalty prompts",
        ],
        // Real-world example concepts for the Event/Offer Monthly Strategy.
        examples: [
            {
                title: "Blind Wine Tasting Challenge",
                body: "Brown-bagged mystery pours and a guided tasting that teaches guests to identify wines by sight, smell and structure — like a true sommelier. Equal parts education and entertainment, and a proven way to fill a slow weeknight with high-spending regulars.",
                tag: "Wine bar",
            },
            {
                title: "Sunday Supper Club",
                body: "A ticketed, communal-table dinner with a chef-driven set menu — one seating, shared plates, real conversation between strangers. Sells out weeks in advance, creates a sense of insider-only community, and turns first-timers into regulars.",
                tag: "Restaurant",
            },
            {
                title: "An Evening in España — Paella, Spanish Wines & Flamenco Guitar",
                body: "Spanish delicacies paired with wines from across the regions of Spain, set to live flamenco guitar. A themed, single-night menu that anchors the room and gives guests a story to tell — easy to re-skin into other immersive country-themed nights once the format clicks.",
                tag: "Restaurant",
            },
            {
                title: "Caviar, Blinis & Bubbles Tasting",
                body: "A walk-around tasting of caviar service, hand-passed blinis and a curated flight of sparkling wines and Champagnes. Luxe, photogenic, and a proven driver of high-margin holiday-season bookings — ideal for private buyouts and gift-able tickets.",
                tag: "Wine bar / Restaurant",
            },
        ],
    },
    {
        id: "loyalty",
        tag: "One-Time",
        name: "Loyalty Program Setup",
        price: "$500",
        cadence: "one-time",
        desc: "A structured loyalty program designed to encourage repeat visits, increase retention, and turn occasional customers into regulars.",
        goal: "Increase visit frequency, lifetime guest value and build loyalty with an automated rewards engine.",
        heroImage: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?fm=jpg&q=85&w=1400&auto=format&fit=crop",
        heroAlt: "Warm, modern cafe interior with a barista behind the counter — the kind of place regulars return to again and again",
        includes: [
            "Program concept & reward structure",
            "POS / software setup guidance",
            "Customer-facing copy & signage",
            "Staff training & talking points",
            "Launch strategy & sign-up campaign",
            "Tracking & performance review",
        ],
        examples: [
            {
                title: "The Regulars' Table",
                body: "A points-based program where every dollar spent earns 1 point, with rewards unlocking at 100 (free dessert), 250 (chef's amuse on the house), and 500 (priority reservations + a complimentary glass of wine). Drives a measurable lift in repeat visits within the first 60 days.",
                tag: "Restaurant",
            },
            {
                title: "Cork Club Tiers",
                body: "A three-tier wine-loyalty program — Bronze, Silver, Gold — based on annual spend. Each tier unlocks perks like free corkage, member-only tastings, allocation rights on rare bottles, and birthday-month bonus pours. Turns big spenders into your biggest fans.",
                tag: "Wine bar",
            },
            {
                title: "The Brewer's Pass",
                body: "A punch-card-style program where every 10th pint is on the house, plus member-only pre-releases of limited beers and 10% off growler fills. Simple, sticky, and proven to lift visit frequency 20-30% among existing customers.",
                tag: "Brewery",
            },
            {
                title: "The Baker's Dozen Club",
                body: "A café or bakery loyalty program where guests earn rewards for pastries, bread, breakfast sandwiches, or coffee-and-baked-good combos. Every 12th item unlocks a free treat or a discounted bakery box. Simple, memorable, and easy for staff to explain at the counter.",
                tag: "Café / Bakery",
            },
        ],
    },
    {
        id: "subscription",
        tag: "One-Time",
        name: "Monthly Subscription Box Setup",
        price: "$500",
        cadence: "one-time",
        desc: "A recurring revenue program — wine clubs, meal kits, subscription boxes, tasting clubs, or member perks — that creates steady monthly income.",
        goal: "Generate predictable, recurring monthly revenue and a built-in community of your most loyal regulars.",
        heroImage: "https://customer-assets.emergentagent.com/job_drink-food-expert/artifacts/1ejmb5zj_jon-koop-T1lLp2vO4TU-unsplash.jpg",
        heroAlt: "Four chilled, condensation-covered craft beer bottles lined up on a sunlit wooden table, blurred greenery behind — the curated monthly drop of a subscription beer club",
        includes: [
            "Subscription concept & pricing",
            "Membership tiers & fulfillment workflow",
            "POS, website & payment setup",
            "Sales copy across channels",
            "Staff training & launch campaign",
            "Retention & long-term value strategy",
        ],
        // Inspired by what Table22 enables for restaurants (Ava Gene's pasta club,
        // wine clubs, supper clubs, butcher boxes, bakery subscriptions, etc.)
        examples: [
            {
                title: "Pasta Drop — Dinner for Two",
                body: "Members receive a monthly dinner-for-two kit — two house-made pastas, a signature sauce, finishing cheese, and a recipe card from the chef. A bottle of wine can be added on for an additional charge. Billed monthly, picked up or delivered locally — a proven model (à la Ava Gene's) that turns dining-room demand into predictable kitchen revenue.",
                tag: "Restaurant",
            },
            {
                title: "Cellar Society Wine Club",
                body: "Curated bottles delivered monthly, with a complimentary glass on pick-up and a standing member discount on any additional bottle purchases. Predictable monthly cash flow plus a built-in reason for members to keep walking through the door.",
                tag: "Wine bar",
            },
            {
                title: "The Brewer's Circle",
                body: "A monthly four-pack of limited and members-only beers (some never sold on tap), with brewer notes, exclusive merch drops, and first dibs on event tickets. Members become your most reliable taproom traffic — and your loudest advocates.",
                tag: "Brewery",
            },
            {
                title: "Bakery Bundle",
                body: "A monthly bundle featuring signature breads and a rotating mix of sweet and savoury pastries — baked fresh for members only. Builds a recurring revenue stream from the same flour and oven hours you're already running, and gives regulars a reason to come in every single month.",
                tag: "Café / Bakery",
            },
        ],
    },
];

const hourly = [
    "Revenue & sales growth strategy",
    "Private event & catering sales",
    "Lead generation strategy",
    "Operational SOPs & checklists",
    "Financial review & profitability",
    "Menu pricing & product mix",
    "POS, CRM & systems setup",
    "HR, onboarding & training",
    "Brand refresh & overhaul",
    "Website & guest journey review",
    "Promotional calendar planning",
    "Owner/operator efficiency systems",
];

export default function Packages() {
    const [openExamplesId, setOpenExamplesId] = useState(null);
    const openPkg = packages.find((p) => p.id === openExamplesId);

    return (
        <section
            id="packages"
            data-testid="packages-section"
            className="py-24 md:py-32 bg-white border-t border-brand-border-light"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-6 mb-16 md:mb-20">
                    <div className="col-span-12 md:col-span-4 flex items-center gap-4">
                        <span className="h-px w-10 bg-brand-orange" />
                        <span className="text-xs uppercase tracking-[0.3em] text-brand-orange font-medium">
                            Packages & Pricing
                        </span>
                    </div>
                    <h2
                        data-testid="packages-headline"
                        className="col-span-12 md:col-span-8 font-serif text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight text-brand-dark"
                    >
                        Clear, transparent
                        <br />
                        <span className="italic">pricing &amp; packages.</span>
                    </h2>
                    <p
                        data-testid="packages-subhead"
                        className="col-span-12 md:col-start-5 md:col-span-8 text-lg md:text-xl text-brand-muted font-light leading-relaxed max-w-2xl mt-6"
                    >
                        Three packages built to drive recurring revenue quietly in the
                        background — so you stay focused on running your business while the
                        programs do the heavy lifting.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-brand-border-light border border-brand-border-light">
                    {packages.map((p, i) => (
                        <motion.div
                            key={p.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            data-testid={`package-card-${p.id}`}
                            className="bg-white p-8 md:p-10 flex flex-col"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <span className="text-[10px] uppercase tracking-[0.3em] text-brand-orange border border-brand-orange px-3 py-1">
                                    {p.tag}
                                </span>
                                <span className="font-serif text-brand-muted italic text-sm">
                                    0{i + 1}
                                </span>
                            </div>

                            <h3 className="font-serif text-3xl md:text-4xl text-brand-dark leading-tight mb-4">
                                {p.name}
                            </h3>

                            <div className="flex items-baseline gap-2 mb-6 pb-6 border-b border-brand-border-light">
                                <span className="font-serif text-5xl text-brand-orange">
                                    {p.price}
                                </span>
                                <span className="text-sm text-brand-muted">{p.cadence}</span>
                            </div>

                            <p className="text-brand-muted leading-relaxed mb-6">{p.desc}</p>

                            <div
                                data-testid={`package-goal-${p.id}`}
                                className="mb-6 pb-6 border-b border-brand-border-light"
                            >
                                <span className="block text-[10px] uppercase tracking-[0.3em] text-brand-orange mb-2">
                                    The Goal
                                </span>
                                <p className="font-serif italic text-brand-dark leading-relaxed text-lg">
                                    {p.goal}
                                </p>
                            </div>

                            <ul className="space-y-3 mb-8 flex-1">
                                {p.includes.map((it) => (
                                    <li
                                        key={it}
                                        className="text-sm text-brand-dark flex items-start gap-3 leading-relaxed"
                                    >
                                        <span className="text-brand-orange mt-1.5 text-xs">▸</span>
                                        {it}
                                    </li>
                                ))}
                            </ul>

                            {p.examples && p.examples.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setOpenExamplesId(p.id)}
                                    data-testid={`package-examples-${p.id}`}
                                    className="group inline-flex items-center justify-between gap-2 w-full bg-brand-orange/10 border border-brand-orange text-brand-orange px-5 py-3.5 text-xs uppercase tracking-[0.25em] font-medium hover:bg-brand-orange hover:text-brand-cream transition-colors mb-5"
                                >
                                    <span className="flex items-center gap-2">
                                        <span className="inline-block w-1.5 h-1.5 bg-brand-orange group-hover:bg-brand-cream transition-colors" />
                                        See Example Concepts
                                    </span>
                                    <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                                </button>
                            )}

                            <a
                                href="#contact"
                                data-testid={`package-cta-${p.id}`}
                                className="inline-flex items-center justify-between gap-2 border border-brand-dark text-brand-dark px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-brand-dark hover:text-brand-cream transition-colors group"
                            >
                                Inquire
                                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                            </a>
                        </motion.div>
                    ))}
                </div>

                {/* Hourly */}
                <div
                    data-testid="hourly-section"
                    className="mt-16 md:mt-20 bg-brand-dark text-brand-cream p-5 sm:p-10 md:p-16 grid grid-cols-12 gap-6 md:gap-8"
                >
                    <div className="col-span-12 md:col-span-5 min-w-0">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-brand-orange">
                            Additional Consulting
                        </span>
                        <h3 className="font-serif text-[28px] sm:text-4xl md:text-5xl mt-4 mb-6 leading-tight break-words">
                            Targeted support, <span className="italic">by the hour.</span>
                        </h3>
                        <div className="flex items-baseline gap-3 mb-6">
                            <span className="font-serif text-5xl sm:text-6xl text-brand-orange">$45</span>
                            <span className="text-sm uppercase tracking-[0.2em] text-brand-cream/70">
                                per hour
                            </span>
                        </div>
                        <p className="text-brand-cream/70 leading-relaxed mb-6 max-w-md">
                            For clients who need targeted support beyond a standard package — one-time
                            projects, ongoing strategy, operational cleanup, or hands-on help building
                            systems.
                        </p>
                        <button
                            type="button"
                            onClick={() => openCalendly("60min", "hourly-book")}
                            data-testid="hourly-cta"
                            className="inline-flex items-center gap-2 bg-brand-orange text-brand-cream px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-brand-orange-hover transition-colors cursor-pointer"
                        >
                            Book an Hour
                        </button>
                    </div>

                    <div className="col-span-12 md:col-span-7 min-w-0">
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8">
                            {hourly.map((h, idx) => (
                                <li
                                    key={h}
                                    className="menu-row text-brand-cream/90 font-serif text-lg"
                                >
                                    <span>{h}</span>
                                    <span className="leader" />
                                    <span className="text-xs text-brand-cream/40 font-sans">
                                        0{idx + 1 < 10 ? `${idx + 1}` : idx + 1}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            <PackageExamplesModal
                open={!!openExamplesId}
                onClose={() => setOpenExamplesId(null)}
                packageName={openPkg?.name || ""}
                examples={openPkg?.examples || []}
                heroImage={openPkg?.heroImage}
                heroAlt={openPkg?.heroAlt}
            />
        </section>
    );
}
